import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    MenuItem,
    Paper,
    Radio,
    Slide,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from "@mui/material"
import { TransitionProps } from "@mui/material/transitions"
import { DatePicker } from "@mui/x-date-pickers"
import dayjs, { Dayjs } from "dayjs"
import "dayjs/locale/es"
import { forwardRef, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

const SlideUpTransition = forwardRef(function SlideUpTransition(
    props: TransitionProps & { children: React.ReactElement<unknown> },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />
})

export interface IOrderOption {
    id: number
    order_code: string
    // Only the export flow reads these two.
    max_reference_number?: number | null
    client_name: string
    project_name: string
    location: string
    status: string
    reference_name: string | null
    reference_width: number | null
    reference_height: number | null
    reference_paper_color: number | null
    general_info_programmed_total: number | null
    general_info_requested_amount: number | null
    general_info_programmed_kilograms?: number | null
    printing_inks_amount: number | null
    order_entry_date: string | null
}

interface IPagination {
    current_page: number
    total_pages: number
    total_count: number
    per_page: number
}

interface IFilters {
    q: string
    entryDate: Dayjs | null
    status: "active" | "inactive" | ""
    paperColor: 0 | 1 | 2 | ""
}

const PAPER_COLOR_KEYS: Record<number, string> = { 0: "na", 1: "white", 2: "kraft" }

const formatOrderCode = (code: string) =>
    code.replace(/^OF-ST(\d+)-/, (_, m) => `OFT-ST${m.padStart(2, "0")}-`)

const initialFilters = (): IFilters => ({
    q: "",
    entryDate: dayjs(),
    status: "active",
    paperColor: "",
})

type Props = {
    open: boolean
    onClose: () => void
    onConfirm: (orders: IOrderOption[]) => void
    excludedOrderIds: number[]
    // When true the picker allows selecting several orders at once (bulk add).
    // When false (default) it behaves as a single-select linker for one row.
    multiple?: boolean
}

export default function OrderPickerDialog({ open, onClose, onConfirm, excludedOrderIds, multiple = false }: Props) {
    const { t, i18n } = useTranslation()

    const [filters, setFilters] = useState<IFilters>(initialFilters)
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(25)
    const [data, setData] = useState<IOrderOption[]>([])
    const [pagination, setPagination] = useState<IPagination>({ current_page: 1, total_pages: 1, total_count: 0, per_page: 25 })
    const [loading, setLoading] = useState(false)
    const [errorKey, setErrorKey] = useState<string | null>(null)
    const [retryNonce, setRetryNonce] = useState(0)
    const [selectedById, setSelectedById] = useState<Map<number, IOrderOption>>(new Map())

    // Debounce text inputs into the filters state
    const [qInput, setQInput] = useState("")
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // Reset on open transition
    useEffect(() => {
        if (open) {
            const f = initialFilters()
            setFilters(f)
            setQInput("")
            setPage(1)
            setPerPage(25)
            setSelectedById(new Map())
            setErrorKey(null)
        }
    }, [open])

    // Push debounced text into filters. Bail out when the input already matches
    // filters so the open-transition reset does not produce a duplicate fetch.
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
            setFilters(prev => prev.q === qInput ? prev : { ...prev, q: qInput })
            setPage(1)
        }, 350)
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [qInput])

    // Fetch. excludeKey is the canonical projection of excludedOrderIds — depending
    // on it (instead of the array itself) avoids re-fetching when the parent
    // re-renders with a new array identity but the same contents.
    const excludeKey = useMemo(
        () => excludedOrderIds.slice().sort((a, b) => a - b).join(","),
        [excludedOrderIds]
    )
    useEffect(() => {
        if (!open) return
        const controller = new AbortController()
        const params = new URLSearchParams()
        if (filters.q) params.set("q", filters.q)
        if (filters.entryDate) {
            params.set("month", String(filters.entryDate.month() + 1))
            params.set("year", String(filters.entryDate.year()))
        }
        if (filters.status !== "") params.set("status", filters.status)
        if (filters.paperColor !== "") params.set("paper_color", String(filters.paperColor))
        params.set("page", String(page))
        params.set("per_page", String(perPage))
        const excludeList = excludeKey ? excludeKey.split(",") : []
        excludeList.forEach(id => params.append("exclude_ids[]", id))

        setLoading(true)
        setErrorKey(null)
        fetch(`/orders/search?${params.toString()}`, { signal: controller.signal })
            .then(r => {
                if (!r.ok) throw new Error("HTTP " + r.status)
                return r.json()
            })
            .then((body: { orders: IOrderOption[]; pagination: IPagination }) => {
                setData(body.orders)
                setPagination(body.pagination)
            })
            .catch(err => {
                if (err.name === "AbortError") return
                setErrorKey("loadError")
            })
            .finally(() => setLoading(false))

        return () => controller.abort()
    }, [open, filters, page, perPage, excludeKey, retryNonce])

    // Toggling a row. In single-select mode the selection collapses to just
    // the clicked order; in multiple mode it adds/removes from the set.
    const toggleRow = (order: IOrderOption) => {
        setSelectedById(prev => {
            if (!multiple) {
                if (prev.size === 1 && prev.has(order.id)) return new Map()
                return new Map([[order.id, order]])
            }
            const next = new Map(prev)
            if (next.has(order.id)) next.delete(order.id)
            else next.set(order.id, order)
            return next
        })
    }

    const visibleIds = data.map(o => o.id)
    const visibleSelectedCount = visibleIds.filter(id => selectedById.has(id)).length
    const allVisibleSelected = data.length > 0 && visibleSelectedCount === data.length
    const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected

    const toggleAllVisible = () => {
        setSelectedById(prev => {
            const next = new Map(prev)
            if (allVisibleSelected) {
                data.forEach(o => next.delete(o.id))
            } else {
                data.forEach(o => next.set(o.id, o))
            }
            return next
        })
    }

    const handleConfirm = () => {
        if (selectedById.size === 0) return
        onConfirm(Array.from(selectedById.values()))
        onClose()
    }

    const selectedCount = selectedById.size

    return (
        <Dialog open={open} onClose={onClose} fullScreen slots={{ transition: SlideUpTransition }}>
            <DialogTitle>{t("extrusion.form.picker.title")}</DialogTitle>
            <DialogContent dividers sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr 1fr", md: "2fr 1fr 1fr 1fr" },
                    gap: 2,
                    mb: 2,
                }}>
                    <TextField
                        size="small"
                        label={t("extrusion.form.picker.search")}
                        value={qInput}
                        onChange={e => setQInput(e.target.value)}
                    />
                    <DatePicker
                        label={t("extrusion.form.picker.entryDate")}
                        value={filters.entryDate}
                        onAccept={val => { setFilters(f => ({ ...f, entryDate: val })); setPage(1) }}
                        views={["year", "month"]}
                        format="MMMM YYYY"
                        slotProps={{
                            textField: { size: "small", fullWidth: true },
                            field: { clearable: true },
                        }}
                    />
                    <TextField
                        size="small"
                        select
                        label={t("extrusion.form.picker.status")}
                        value={filters.status}
                        onChange={e => { setFilters(f => ({ ...f, status: e.target.value as IFilters["status"] })); setPage(1) }}
                        slotProps={{
                            select: { displayEmpty: true },
                            inputLabel: { shrink: true },
                        }}
                    >
                        <MenuItem value="">{t("extrusion.form.picker.allStatuses")}</MenuItem>
                        <MenuItem value="active">{t("orders.statuses.active")}</MenuItem>
                        <MenuItem value="inactive">{t("orders.statuses.inactive")}</MenuItem>
                    </TextField>
                    <TextField
                        size="small"
                        select
                        label={t("extrusion.form.picker.paperColor")}
                        value={filters.paperColor}
                        onChange={e => { setFilters(f => ({ ...f, paperColor: e.target.value === "" ? "" : Number(e.target.value) as IFilters["paperColor"] })); setPage(1) }}
                        slotProps={{
                            select: { displayEmpty: true },
                            inputLabel: { shrink: true },
                        }}
                    >
                        <MenuItem value="">{t("extrusion.form.picker.allColors")}</MenuItem>
                        <MenuItem value={0}>{t("orders.form.color.na")}</MenuItem>
                        <MenuItem value={1}>{t("orders.form.color.white")}</MenuItem>
                        <MenuItem value={2}>{t("orders.form.color.kraft")}</MenuItem>
                    </TextField>
                </Box>

                {errorKey && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2 }}
                        action={
                            <Button color="inherit" size="small" onClick={() => setRetryNonce(n => n + 1)}>
                                {t("extrusion.form.picker.retry")}
                            </Button>
                        }
                    >
                        {t(`extrusion.form.picker.${errorKey}`)}
                    </Alert>
                )}

                <Paper variant="outlined" sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                    <Box sx={{ height: 4 }}>{loading && <LinearProgress />}</Box>
                    <Box sx={{ flex: 1, overflow: "auto" }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        {multiple && (
                                            <Checkbox
                                                indeterminate={someVisibleSelected}
                                                checked={allVisibleSelected}
                                                onChange={toggleAllVisible}
                                                disabled={data.length === 0}
                                            />
                                        )}
                                    </TableCell>
                                    {[
                                        t("orders.project"),
                                        t("orders.client"),
                                        t("orders.code"),
                                        t("orders.form.location"),
                                        t("extrusion.form.reference"),
                                        t("extrusion.form.materialColor"),
                                        t("extrusion.form.kilos"),
                                        t("extrusion.form.units"),
                                        t("orders.color"),
                                        t("extrusion.form.entryDate"),
                                    ].map((label, i) => (
                                        <TableCell key={i} sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>{label}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.map(order => {
                                    const checked = selectedById.has(order.id)
                                    return (
                                        <TableRow key={order.id} hover selected={checked} onClick={() => toggleRow(order)} sx={{ cursor: "pointer" }}>
                                            <TableCell padding="checkbox">
                                                {multiple ? (
                                                    <Checkbox checked={checked} onChange={() => toggleRow(order)} onClick={e => e.stopPropagation()} />
                                                ) : (
                                                    <Radio checked={checked} onChange={() => toggleRow(order)} onClick={e => e.stopPropagation()} />
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    {order.project_name}
                                                    {order.status === "inactive" && (
                                                        <Chip label={t("orders.statuses.inactive")} size="small" color="warning" variant="outlined" />
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>{order.client_name}</TableCell>
                                            <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem", whiteSpace: "nowrap" }}>{formatOrderCode(order.order_code)}</TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>{order.location || "—"}</TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                {order.reference_name
                                                    ? `${order.reference_name}${order.reference_width != null ? ` ${order.reference_width} x ${order.reference_height}` : ""}`
                                                    : "—"}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>{order.reference_paper_color != null ? t(`orders.form.color.${PAPER_COLOR_KEYS[order.reference_paper_color] ?? "na"}`) : "—"}</TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{order.general_info_programmed_total != null ? order.general_info_programmed_total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}</TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{order.general_info_requested_amount != null ? order.general_info_requested_amount.toLocaleString("en-US") : "—"}</TableCell>
                                            <TableCell align="center">{order.printing_inks_amount ?? "—"}</TableCell>
                                            <TableCell sx={{ textTransform: "capitalize", whiteSpace: "nowrap" }}>{order.order_entry_date ? dayjs(order.order_entry_date).locale(i18n.language).format("MMMM") : "—"}</TableCell>
                                        </TableRow>
                                    )
                                })}
                                {!loading && data.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={11} align="center" sx={{ color: "text.secondary", py: 3 }}>
                                            {t("extrusion.form.picker.noResults")}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                    <TablePagination
                        component="div"
                        count={pagination.total_count}
                        page={pagination.current_page - 1}
                        onPageChange={(_, newPage) => setPage(newPage + 1)}
                        rowsPerPage={perPage}
                        onRowsPerPageChange={e => { setPerPage(Number(e.target.value)); setPage(1) }}
                        rowsPerPageOptions={[25, 50, 100]}
                    />
                </Paper>
            </DialogContent>
            <DialogActions>
                {multiple && (
                    <Typography variant="body2" color="text.secondary" sx={{ mr: "auto", ml: 2 }}>
                        {t("extrusion.form.picker.selectedCount", { count: selectedCount })}
                    </Typography>
                )}
                <Button color="inherit" onClick={onClose}>{t("common.cancel")}</Button>
                <Button variant="contained" onClick={handleConfirm} disabled={selectedCount === 0}>
                    {multiple
                        ? t("extrusion.form.picker.addCount", { count: selectedCount })
                        : t("export.form.entry.action.link")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
